import React, { useState, useRef, useContext, useEffect, useCallback } from "react";
import { MdAttachFile } from "react-icons/md";
import { FaMicrophone } from "react-icons/fa";
import { HiOutlineDotsVertical } from "react-icons/hi";
import EditableDiv from "./chat/EditableDiv";
import AudioRecorder from "./audio/AudioRecorder";
import PreviewAudio from "./audio/PreviewAudio";
import { DisplayDiscussionForMiniScreenContext } from "../contexts/DisplayDiscussionForMiniScreenContext";
import { AuthContext } from "../contexts/AuthContext";
import { WorkspaceContext } from "../contexts/WorkspaceContext";
import { env } from "../datas.config";
import "./audio/audio-components.css";
import {
  sendMessageGroupe,
  sendMessageFileGroupe,
  getFileSize,
} from "../services/sendMessageGroupe";
import {
  sendMessageInbox,
  sendMessageFileInbox,
} from "../services/sendMessageInbox";

export default function ChatFormWithAudio({ conversation, isMuted, replyTo, onClearReply }) {
  const { destinataire, crew, inboxRecipientRole } = useContext(
    DisplayDiscussionForMiniScreenContext
  );
  const { user } = useContext(AuthContext);
  const { currentUserRole, currentWorkspace, hasPermission } = useContext(WorkspaceContext);
  const isGuest = currentUserRole === "guest";
  const workspaceId = currentWorkspace?._id;
  const canWrite = hasPermission("CAN_WRITE_A_MESSAGE");
  const contentRef = useRef(null);
  const [files, setFiles] = useState([]);
  const [filesSelected, setFilesSelected] = useState(false);

  const [recordingMode, setRecordingMode] = useState(false);
  const [recordingBlob, setRecordingBlob] = useState(null);
  const [recordingDuration, setRecordingDuration] = useState(0);
  const [recordingPeaks, setRecordingPeaks] = useState([]);
  const [recordingExt, setRecordingExt] = useState("webm");
  const [isSendingAudio, setIsSendingAudio] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const menuRef = useRef(null);

  const getRoomId = () => {
    if (conversation === "inbox") {
      return user.userId > destinataire.userId
        ? `${user.userId}_${destinataire.userId}`
        : `${destinataire.userId}_${user.userId}`;
    }
    return crew.roomId;
  };

  const getDestinataire = () => {
    if (conversation === "inbox") return destinataire.userId;
    return crew.membres.filter((member) => member.userId !== user.userId);
  };

  const handleSubmit = (e) => {
    const content = contentRef.current.innerText;
    const contentTrimed = content.trim();
    const msg = {
      conversation,
      emetteur: user.userId,
      emetteurImage: user.userImage,
      destinataire: getDestinataire(),
      contenu: content,
      roomId: getRoomId(),
      workspaceId,
      ...(replyTo && {
        replyToId: replyTo.replyToId,
        replyToContenu: replyTo.replyToContenu,
        replyToSender: replyTo.replyToSender,
        replyToNature: replyTo.replyToNature,
      }),
    };

    if (content.length > 0 && contentTrimed !== "") {
      contentRef.current.innerText = "";
      conversation === "inbox" ? sendMessageInbox(msg, workspaceId) : sendMessageGroupe(msg, workspaceId);
      if (onClearReply) onClearReply();
    } else if (filesSelected && files.length > 0) {
      const timeUnix = Date.now();
      const formData = new FormData();
      const contenuArray = [];
      let tooLarge = false;

      files.forEach((f) => {
        if (f.size > 50 * 1024 * 1024) {
          tooLarge = true;
        }
        formData.append("files", f);
        contenuArray.push({
          nom: f.name,
          taille: getFileSize(f.size),
          fileUrl: "",
        });
      });

      if (tooLarge) {
        alert("UN OU PLUSIEURS FICHIERS DÉPASSENT 50Mo");
        return;
      }

      formData.append("emetteur", msg.emetteur);
      formData.append("destinataire", JSON.stringify(msg.destinataire));
      formData.append("contenu", JSON.stringify(contenuArray));
      formData.append("timeUnix", timeUnix);
      formData.append("canal", msg.roomId);

      const msgForSocket = {
        contenu: contenuArray,
        emetteur: msg.emetteur,
        destinataire: msg.destinataire,
        roomId: msg.roomId,
        timeUnix,
        isGroupe: conversation === "inbox" ? false : true,
        workspaceId,
        ...(replyTo && {
          replyToId: replyTo.replyToId,
          replyToContenu: replyTo.replyToContenu,
          replyToSender: replyTo.replyToSender,
          replyToNature: replyTo.replyToNature,
        }),
      };

      conversation === "inbox"
        ? sendMessageFileInbox(msg, formData, msgForSocket, workspaceId)
        : sendMessageFileGroupe(msg, formData, msgForSocket, workspaceId);

      setFiles([]);
      setFilesSelected(false);
      if (onClearReply) onClearReply();
    }
  };

  const handleRecordingComplete = (blob, duration, peaks, ext) => {
    setRecordingBlob(blob);
    setRecordingDuration(duration);
    setRecordingPeaks(peaks);
    setRecordingExt(ext || "webm");
  };

  const handleSendAudio = async () => {
    if (!recordingBlob || !workspaceId) return;
    setIsSendingAudio(true);

    const ext = recordingExt;
    const roomId = getRoomId();
    const formData = new FormData();
    formData.append("audio", recordingBlob, `vocal_${Date.now()}.${ext}`);
    formData.append("meta", JSON.stringify({
      emetteur: user.userId,
      destinataire: getDestinataire(),
      roomId,
      workspaceId,
      duree: recordingDuration,
      peaks: recordingPeaks,
      timeUnix: Date.now(),
      conversation,
      ...(replyTo && {
        replyToId: replyTo.replyToId,
        replyToContenu: replyTo.replyToContenu,
        replyToSender: replyTo.replyToSender,
        replyToNature: replyTo.replyToNature,
      }),
    }));

    try {
      await fetch(`${env.domain}/api/auth/audio/send`, {
        method: "POST",
        body: formData,
        credentials: "include",
      });
    } catch (err) {
      console.error("Erreur envoi vocal:", err);
    }

    setRecordingBlob(null);
    setRecordingPeaks([]);
    setRecordingDuration(0);
    setRecordingMode(false);
    setIsSendingAudio(false);
    if (onClearReply) onClearReply();
  };

  const handleCancelAudio = () => {
    setRecordingBlob(null);
    setRecordingPeaks([]);
    setRecordingDuration(0);
    setRecordingExt("webm");
    setRecordingMode(false);
  };

  useEffect(() => {
    if (!menuOpen) return;
    const handleClickOutside = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target)) {
        setMenuOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [menuOpen]);

  const handleFileChange = (event) => {
    const selectedFiles = Array.from(event.target.files);
    if (selectedFiles.length > 0) {
      const validFiles = selectedFiles
        .slice(0, 10)
        .filter((f) => f.size <= 50 * 1024 * 1024);
      if (validFiles.length < selectedFiles.length) {
        alert("Certains fichiers ont été ignorés (max 10 fichiers, 50Mo par fichier)");
      }
      setFilesSelected(true);
      setFiles(validFiles);
    }
  };

  if (isGuest) {
    return (
      <div className="tyn-chat-form d-flex align-items-center justify-content-center py-3">
        <span className="text-muted fst-italic">
          Mode lecture seule — les invités ne peuvent pas envoyer de messages.
        </span>
      </div>
    );
  }

  if (isMuted) {
    return (
      <div className="tyn-chat-form d-flex align-items-center justify-content-center py-3">
        <span className="text-muted fst-italic">
          Vous êtes en mode lecture seule dans ce groupe.
        </span>
      </div>
    );
  }

  if (!canWrite) {
    return (
      <div className="tyn-chat-form d-flex align-items-center justify-content-center py-3">
        <span className="text-muted fst-italic">
          Vous n'avez pas la permission d'écrire des messages.
        </span>
      </div>
    );
  }

  if (conversation === "inbox" && inboxRecipientRole === null) {
    return (
      <div className="tyn-chat-form d-flex align-items-center justify-content-center py-3">
        <span className="text-muted fst-italic">
          Vérification des permissions...
        </span>
      </div>
    );
  }

  if (conversation === "inbox" && inboxRecipientRole === "owner" && !hasPermission("CAN_WRITE_A_MESSAGE_TO_OWNER")) {
    return (
      <div className="tyn-chat-form d-flex align-items-center justify-content-center py-3">
        <span className="text-muted fst-italic">
          Vous n'avez pas la permission d'écrire au propriétaire.
        </span>
      </div>
    );
  }

  if (conversation === "inbox" && inboxRecipientRole === "admin" && !hasPermission("CAN_WRITE_A_MESSAGE_TO_ADMIN")) {
    return (
      <div className="tyn-chat-form d-flex align-items-center justify-content-center py-3">
        <span className="text-muted fst-italic">
          Vous n'avez pas la permission d'écrire aux administrateurs.
        </span>
      </div>
    );
  }

  if (recordingMode || recordingBlob) {
    return (
      <div className="tyn-chat-form">
        <div className="tyn-chat-form-insert col-2 width-auto-important">
          <ul className="tyn-list-inline gap gap-3">
            <li>
              <button
                className="btn btn-icon btn-light btn-md btn-pill"
                onClick={handleCancelAudio}
                title="Annuler"
                style={{ color: "#ef4444" }}
              >
                <svg viewBox="0 0 24 24" width="18" height="18" fill="currentColor">
                  <path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 19 17.59 13.41 12z"/>
                </svg>
              </button>
            </li>
          </ul>
        </div>
        <div className="tyn-chat-form-enter audio-form-enter">
          {recordingBlob ? (
            <PreviewAudio
              blob={recordingBlob}
              duration={recordingDuration}
              peaks={recordingPeaks}
              onSend={handleSendAudio}
              onCancel={handleCancelAudio}
              isSending={isSendingAudio}
            />
          ) : (
            <AudioRecorder onRecordingComplete={handleRecordingComplete} autoStart={true} />
          )}
        </div>
      </div>
    );
  }

  return (
    <>
      <style>{`
        .chatform-menu-desktop { display: inline-flex; }
        .chatform-menu-mobile { display: none; }
        @media (max-width: 482px) {
          .chatform-menu-desktop { display: none !important; }
          .chatform-menu-mobile { display: inline-flex !important; }
        }
      `}</style>
      <div className="tyn-chat-form">
        <div className="tyn-chat-form-insert col-2 width-auto-important">
          <ul className="tyn-list-inline gap gap-3">
            {/* Desktop: file + mic buttons */}
            <li className="chatform-menu-desktop">
              <label
                htmlFor="fileInputDocument"
                style={{ cursor: "pointer" }}
                className="btn btn-icon btn-light btn-md btn-pill"
                onClick={() => { setFiles([]); setFilesSelected(false); }}
              >
                <MdAttachFile />
              </label>
              <input
                type="file"
                id="fileInputDocument"
                multiple
                accept="image/*,application/*,video/*,audio/*,text/*,.pdf,.doc,.docx,.xls,.xlsx,.txt,.zip,.rar,.7z,.tar"
                style={{ display: "none" }}
                onChange={handleFileChange}
              />
            </li>
            <li className="chatform-menu-desktop">
              <button
                className="btn btn-icon btn-light btn-md btn-pill"
                onClick={() => setRecordingMode(true)}
                title="Message vocal"
              >
                <FaMicrophone />
              </button>
            </li>

            {/* Mobile: dots button + popup */}
            <li className="chatform-menu-mobile" ref={menuRef} style={{ position: "relative" }}>
              <button
                className="btn btn-icon btn-light btn-md btn-pill"
                onClick={() => setMenuOpen((v) => !v)}
                title="Plus d'options"
              >
                <HiOutlineDotsVertical />
              </button>
              {menuOpen && (
                <div
                  style={{
                    position: "absolute",
                    bottom: "calc(100% + 8px)",
                    left: "0",
                    background: "#fff",
                    borderRadius: "10px",
                    boxShadow: "0 2px 16px rgba(0,0,0,0.2)",
                    padding: "8px 4px",
                    display: "flex",
                    flexDirection: "column",
                    gap: "4px",
                    zIndex: 9999,
                    minWidth: "44px",
                  }}
                >
                  <button
                    className="btn btn-icon btn-light btn-md btn-pill"
                    onClick={() => {
                      setMenuOpen(false);
                      setFiles([]);
                      setFilesSelected(false);
                      document.getElementById("fileInputDocumentMobile").click();
                    }}
                    title="Joindre un fichier"
                  >
                    <MdAttachFile />
                  </button>
                  <input
                    type="file"
                    id="fileInputDocumentMobile"
                    multiple
                    accept="image/*,application/*,video/*,audio/*,text/*,.pdf,.doc,.docx,.xls,.xlsx,.txt,.zip,.rar,.7z,.tar"
                    style={{ display: "none" }}
                    onChange={handleFileChange}
                  />
                  <button
                    className="btn btn-icon btn-light btn-md btn-pill"
                    onClick={() => {
                      setMenuOpen(false);
                      setRecordingMode(true);
                    }}
                    title="Message vocal"
                  >
                    <FaMicrophone />
                  </button>
                </div>
              )}
            </li>
          </ul>
        </div>
        <EditableDiv
          setFilesSelected={setFilesSelected}
          setFiles={setFiles}
          files={{ files, filesSelected }}
          contentRef={contentRef}
          onSubmit={handleSubmit}
          conversation={conversation}
        />
      </div>
    </>
  );
}
